import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  postAdminResetPersonInvitationEmail,
  postAdminSendPersonInvitation,
} from "../../api/adminEmail";
import { fetchAdminPersons, inviteAdminPerson } from "../../api/adminPersons";
import AdminAppLayout from "../../components/admin/AdminAppLayout";

function personLabel(p) {
  if (!p) return "—";
  const n = String(p.fullName || "").trim();
  return n || String(p.email || "").trim() || String(p.id || "").slice(0, 8);
}

function PairingCell({ person, byId }) {
  const primaryId = person.plusOneOfPersonId;
  const plusId = person.plusOnePersonId;
  if (primaryId) {
    const primary = byId.get(primaryId);
    return (
      <span className="admin-guests-pairing">
        <span className="admin-guests-pairing-badge">Plus-one</span>
        <span className="admin-guests-pairing-text">of {personLabel(primary)}</span>
      </span>
    );
  }
  if (plusId) {
    const guest = byId.get(plusId);
    return (
      <span className="admin-guests-pairing">
        <span className="admin-guests-pairing-text">
          <strong>+1:</strong> {personLabel(guest)}
        </span>
      </span>
    );
  }
  return <span className="admin-guests-muted">—</span>;
}

function statusLabel(status) {
  const s = String(status || "guest").toLowerCase();
  if (s === "guest") return "Guest";
  if (s === "invited") return "Invited";
  if (s === "attendee") return "Attendee";
  return s;
}

export default function AdminGuestsPage() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [emailBusyId, setEmailBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { persons: next } = await fetchAdminPersons();
      setPersons(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load people.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const personById = useMemo(() => {
    const m = new Map();
    for (const p of persons) {
      if (p?.id) m.set(p.id, p);
    }
    return m;
  }, [persons]);

  async function handleInvite(id) {
    setError(null);
    setNotice(null);
    setBusyId(id);
    try {
      const out = await inviteAdminPerson(id);
      const person = out.person;
      setPersons((prev) => prev.map((p) => (p.id === person.id ? { ...p, ...person } : p)));
      if (out.invitationEmail?.sent) {
        setNotice("Invitation email was sent automatically.");
      } else if (out.invitationEmail?.reason === "auto_send_disabled") {
        setNotice("Marked invited. Turn on auto-send on Email test, or send from this row / bulk send.");
      } else if (out.invitationEmail?.reason === "ses_not_configured") {
        setNotice("Marked invited. SES is not configured — invitation email was not sent.");
      } else if (out.invitationEmail?.error) {
        setNotice(`Marked invited. Invitation email error: ${out.invitationEmail.error}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleSendInvite(personId, force) {
    setError(null);
    setNotice(null);
    setEmailBusyId(personId);
    try {
      const out = await postAdminSendPersonInvitation(personId, { force });
      if (out.sent) {
        setNotice(force ? "Invitation resent." : "Invitation sent.");
      } else if (out.reason === "already_sent") {
        setNotice("Already sent. Use Resend or Reset.");
      } else {
        setNotice("Invitation was not sent.");
      }
      const { persons: next } = await fetchAdminPersons();
      setPersons(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Send failed.");
    } finally {
      setEmailBusyId(null);
    }
  }

  async function handleResetInvite(personId) {
    if (!window.confirm("Clear invitation send flags for this person?")) return;
    setError(null);
    setNotice(null);
    setEmailBusyId(personId);
    try {
      await postAdminResetPersonInvitationEmail(personId);
      setNotice("Invitation flags cleared for this person.");
      const { persons: next } = await fetchAdminPersons();
      setPersons(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reset failed.");
    } finally {
      setEmailBusyId(null);
    }
  }

  return (
    <AdminAppLayout>
      <header className="admin-app-pagehead admin-app-pagehead--split">
        <div>
          <p className="admin-dashboard-status">Admin</p>
          <h1 className="admin-app-title">People</h1>
          <p className="admin-guests-lead admin-guests-lead--tight">
            Everyone except other administrators. Mark <strong>guest</strong> entries as{" "}
            <strong>invited</strong> when you are ready to shortlist them for formal invitations.
          </p>
        </div>
        <Link to="/admin" className="admin-app-back">
          Dashboard
        </Link>
      </header>

      <section className="admin-panel">
        {error ? (
          <p className="admin-login-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="admin-email-test-success" role="status">
            {notice}
          </p>
        ) : null}
        {loading ? (
          <p className="admin-guests-muted">Loading…</p>
        ) : persons.length === 0 ? (
          <p className="admin-guests-muted">No people found yet.</p>
        ) : (
          <div className="admin-guests-table-wrap">
            <table className="admin-guests-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Pairing</th>
                  <th scope="col">Email</th>
                  <th scope="col">Status</th>
                  <th scope="col">Invitation email</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {persons.map((p) => (
                  <tr key={p.id}>
                    <td>{p.fullName || "—"}</td>
                    <td>
                      <PairingCell person={p} byId={personById} />
                    </td>
                    <td>{p.email}</td>
                    <td>
                      <span className={`admin-guests-status admin-guests-status--${String(p.status || "guest")}`}>
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td>
                      {String(p.status || "").toLowerCase() === "invited" ||
                      String(p.status || "").toLowerCase() === "attendee" ? (
                        <div className="admin-guests-invite-email-cell">
                          <div
                            className="admin-guests-muted"
                            style={{ marginBottom: "0.25rem" }}
                            title={p.emailInvitationLastError ? String(p.emailInvitationLastError) : undefined}
                          >
                            {p.emailInvitationSent ? "Sent" : "Not sent"}
                            {p.emailInvitationSentAt
                              ? ` · ${new Date(p.emailInvitationSentAt).toLocaleDateString()}`
                              : ""}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                            <button
                              type="button"
                              className="admin-guests-invite-btn"
                              style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem" }}
                              disabled={emailBusyId === p.id}
                              onClick={() => void handleSendInvite(p.id, false)}
                            >
                              Send
                            </button>
                            <button
                              type="button"
                              className="admin-guests-invite-btn"
                              style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem" }}
                              disabled={emailBusyId === p.id}
                              onClick={() => void handleSendInvite(p.id, true)}
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className="admin-guests-invite-btn"
                              style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem" }}
                              disabled={emailBusyId === p.id}
                              onClick={() => void handleResetInvite(p.id)}
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="admin-guests-muted">—</span>
                      )}
                    </td>
                    <td>{p.submittedAt ? new Date(p.submittedAt).toLocaleString() : "—"}</td>
                    <td>
                      {String(p.status || "").toLowerCase() === "guest" ? (
                        <button
                          type="button"
                          className="admin-guests-invite-btn"
                          disabled={busyId === p.id}
                          onClick={() => void handleInvite(p.id)}
                        >
                          {busyId === p.id ? "Saving…" : "Mark invited"}
                        </button>
                      ) : (
                        <span className="admin-guests-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AdminAppLayout>
  );
}
